/**
 * Content Feedback Service
 *
 * API layer for content comments and AI fixes.
 * Backend routes are registered WITHOUT prefix in api_server.py.
 */

import { apiClient } from './apiClient';
import { useTenantStore } from '../stores/useTenantStore';
import type {
	CommentsGroupedByBlock,
	ContentComment,
	CreateCommentRequest,
	AIFix,
	TriggerAIFixRequest,
} from '../types/contentFeedback';

// No fallback to the AgileCo test tenant: a missing client selection is an
// explicit error at request time, not a silent switch to test data.
const getTenantId = (): string => {
	const tenantId = useTenantStore.getState().currentTenant;
	if (!tenantId) throw new Error('No tenant selected');
	return tenantId;
};

export const contentFeedbackService = {
	/** List comments grouped by block_id. */
	async listComments(contentId: string, signal?: AbortSignal): Promise<CommentsGroupedByBlock> {
		const response = await apiClient.get<CommentsGroupedByBlock>(
			`/content/${contentId}/comments?tenant_id=${getTenantId()}`,
			{ signal },
		);
		return response.data;
	},

	/** Create a comment on content. */
	async createComment(
		contentId: string,
		data: CreateCommentRequest,
	): Promise<ContentComment> {
		const response = await apiClient.post<ContentComment>(
			`/content/${contentId}/comments?tenant_id=${getTenantId()}`,
			data,
		);
		return response.data;
	},

	/** Resolve a comment. */
	async resolveComment(
		contentId: string,
		commentId: string,
	): Promise<ContentComment> {
		const response = await apiClient.patch<ContentComment>(
			`/content/${contentId}/comments/${commentId}/resolve?tenant_id=${getTenantId()}`,
		);
		return response.data;
	},

	/** Trigger an AI fix for a comment. */
	async triggerAIFix(
		contentId: string,
		data: TriggerAIFixRequest,
	): Promise<AIFix> {
		const response = await apiClient.post<AIFix>(
			`/content/${contentId}/ai-fixes?tenant_id=${getTenantId()}`,
			data,
		);
		return response.data;
	},

	/** Approve an AI fix, optionally with user-edited content. */
	async approveAIFix(contentId: string, fixId: string, editedContent?: Record<string, unknown>): Promise<AIFix> {
		const body = editedContent ? { edited_content: editedContent } : undefined;
		const response = await apiClient.patch<AIFix>(
			`/content/${contentId}/ai-fixes/${fixId}/approve?tenant_id=${getTenantId()}`,
			body,
		);
		return response.data;
	},

	/** Reject an AI fix. */
	async rejectAIFix(contentId: string, fixId: string): Promise<AIFix> {
		const response = await apiClient.patch<AIFix>(
			`/content/${contentId}/ai-fixes/${fixId}/reject?tenant_id=${getTenantId()}`,
		);
		return response.data;
	},

	/** Revert an applied AI fix. */
	async revertAIFix(contentId: string, fixId: string): Promise<{ reverted: boolean }> {
		const response = await apiClient.patch<{ reverted: boolean }>(
			`/content/${contentId}/ai-fixes/${fixId}/revert?tenant_id=${getTenantId()}`,
		);
		return response.data;
	},

	/** List AI fixes for content. */
	async listAIFixes(contentId: string, status?: string, signal?: AbortSignal): Promise<AIFix[]> {
		const params = new URLSearchParams({ tenant_id: getTenantId() });
		if (status) params.set('status', status);
		const response = await apiClient.get<AIFix[]>(
			`/content/${contentId}/ai-fixes?${params.toString()}`,
			{ signal },
		);
		return response.data;
	},

	/** Summarize all comments for a content item. */
	async summarizeComments(
		contentId: string,
		includeQAComments: boolean = true,
	): Promise<{ summary: string }> {
		const response = await apiClient.post<{ summary: string }>(
			`/content/${contentId}/comments/summarize?tenant_id=${getTenantId()}`,
			{ include_qa_comments: includeQAComments },
		);
		return response.data;
	},

	/** Teach a comment as a learning pattern. */
	async teachComment(
		contentId: string,
		commentId: string,
		description?: string,
	): Promise<{ message: string; pattern: Record<string, unknown> }> {
		const response = await apiClient.post<{ message: string; pattern: Record<string, unknown> }>(
			`/content/${contentId}/comments/${commentId}/teach?tenant_id=${getTenantId()}`,
			description ? { description } : {},
		);
		return response.data;
	},
};
